"use client";

import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
function HeaderContent({
  isMobileMenuOpen,
  setIsMobileMenuOpen
}: {
  isMobileMenuOpen: boolean;
  setIsMobileMenuOpen: (v: boolean) => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex w-full items-center justify-between">
        <div className="hidden md:flex gap-8">
          <div className="h-4 w-20 animate-pulse rounded bg-muted" />
          <div className="h-4 w-20 animate-pulse rounded bg-muted" />
        </div>
        <div className="h-7 w-32 animate-pulse rounded bg-muted" />
        <div className="hidden md:flex gap-8">
          <div className="h-4 w-20 animate-pulse rounded bg-muted" />
          <div className="h-4 w-20 animate-pulse rounded bg-muted" />
        </div>
      </div>;
  }
  const midPoint = Math.ceil(menuItems.length / 2);
  const leftItems = menuItems.slice(0, midPoint);
  const rightItems = menuItems.slice(midPoint);
  const linkClass = "relative py-2 uppercase tracking-widest text-xs font-default font-semibold text-foreground transition-colors hover:text-primary after:absolute after:bottom-0 after:left-1/2 after:-translate-x-1/2 after:w-0 after:h-[2px] after:bg-primary hover:after:w-full after:transition-all after:duration-300";
  return <>
      {}
      <div className="hidden w-full items-center justify-between md:flex">
        <div className="flex flex-1 justify-start">
          <NavigationMenu viewport={false}>
            <NavigationMenuList className="gap-8">
              {leftItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
                  <NavigationMenuLink asChild className="bg-transparent p-0 hover:bg-transparent focus:bg-transparent data-[active=true]:bg-transparent data-[active=true]:hover:bg-transparent">
                    <Link to={item.href || "#"} className={linkClass}>
                      {item.label}
                    </Link>
                  </NavigationMenuLink>
                </NavigationMenuItem>)}
            </NavigationMenuList>
          </NavigationMenu>
        </div>

        <div className="flex shrink-0 px-8">
          <Link to="/HomePage">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1600149/img/calculo_precision_logo.png" alt="CALCULO" className="h-7 w-auto" />
          </Link>
        </div>

        <div className="flex flex-1 justify-end">
          <NavigationMenu viewport={false}>
            <NavigationMenuList className="gap-8">
              {rightItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
                  <NavigationMenuLink asChild className="bg-transparent p-0 hover:bg-transparent focus:bg-transparent data-[active=true]:bg-transparent data-[active=true]:hover:bg-transparent">
                    <Link to={item.href || "#"} className={linkClass}>
                      {item.label}
                    </Link>
                  </NavigationMenuLink>
                </NavigationMenuItem>)}
            </NavigationMenuList>
          </NavigationMenu>
        </div>
      </div>

      {}
      <div className="flex w-full items-center justify-between md:hidden">
        <Link to="/HomePage">
          <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1600149/img/calculo_precision_logo.png" alt="CALCULO" className="h-6 w-auto" />
        </Link>
        <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle Menu" className="text-foreground hover:bg-accent">
          {isMobileMenuOpen ? <X className="size-5" /> : <Menu className="size-5" />}
        </Button>
      </div>

      {}
      {isMobileMenuOpen && <div className="absolute top-full left-0 flex w-full flex-col gap-4 border-b border-border bg-background/95 backdrop-blur-md p-6 shadow-xl md:hidden">
          {menuItems.map((item, i) => <div key={item.id} data-index={i}>
              <Link to={item.href || "#"} className="block py-2 text-sm font-default font-bold uppercase tracking-widest text-foreground transition-colors hover:text-primary" onClick={() => setIsMobileMenuOpen(false)}>
                {item.label}
              </Link>
            </div>)}
        </div>}
    </>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="227794" id="header" className={`sticky top-0 z-50 w-full transition-all duration-300 bg-background text-foreground ${isScrolled ? "border-b border-border bg-background/80 backdrop-blur-md shadow-sm" : "border-b border-transparent bg-background"}`}>
      <div className="container mx-auto flex h-20 items-center px-4 lg:px-8">
        <MenuProvider menu_id="67" className="flex w-full items-center">
          <HeaderContent isMobileMenuOpen={isMobileMenuOpen} setIsMobileMenuOpen={setIsMobileMenuOpen} />
        </MenuProvider>
      </div>
    </section>;
}